import { useState } from "react";
import { Box, Button, Checkbox, LinearProgress, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { Add, Delete, Edit, FilterList } from "@mui/icons-material";

import { MetaDrivenTable, SearchField, StatusChip } from "../../../shared/components";

export const ResourceRequests = () => {

    const [currentView, setCurrentView] = useState<'list' | 'map'>('list');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleStatusToggle = (status: string) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const handleTypeToggle = (type: string) => {
        setSelectedTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const handleRemoveStatusFilter = (status: string) => {
        setSelectedStatuses(prev => prev.filter(s => s !== status));
    };

    const handleRemoveTypeFilter = (type: string) => {
        setSelectedTypes(prev => prev.filter(t => t !== type));
    };

    const handleClearAllFilters = () => {
        setSelectedStatuses([]);
        setSelectedTypes([]);
    };

    const getFilterLabel = (value: string) => {
        const labels: Record<string, string> = {
            'requested': 'Requested',
            'scheduled': 'Scheduled',
            'delivered': 'Delivered',
            'completed': 'Completed',
            'closed': 'Closed',
            'excavator': 'Excavator',
            'labour': 'Labour',
            'traffic_management': 'Traffic Management',
            'specialist_equipment': 'Specialist Equipment',
            'specialist_contractor': 'Specialist Contractor'
        };
        return labels[value] || value;
    };

    const headers = [
        { "alias_name": "id", "label": "ID", "display_order": 2, is_show: true },
        { "alias_name": "activity", "label": "Activity", "display_order": 2, is_show: true },
        { "alias_name": "resource_type", "label": "Resource Type", "display_order": 2, is_show: true },
        { "alias_name": "description", "label": "Description", "display_order": 2, is_show: true },
        { "alias_name": "duration", "label": "Duration", "display_order": 2, is_show: true },
        { "alias_name": "required_date", "label": "Required Date", "display_order": 2, is_show: true },
        { "alias_name": "status", "label": "Status", "display_order": 2, is_show: true },
        { "alias_name": "progress", "label": "Progress", "display_order": 2, is_show: true },
    ];

    const data = [
        {
            "id": "RES-001",
            "activity": "Main Pipeline Installation - Phase 1",
            "activity_id": "ACT-1001",
            "resource_type": "Excavator",
            "description": "13-tonne Excavator with operator",
            "duration": "5 days",
            "required_date": "2025-12-02",
            "status": "Completed",
            "progress": "100%"
        },
        {
            "id": "RES-002",
            "activity": "Main Pipeline Installation - Phase 1",
            "activity_id": "ACT-1002",
            "resource_type": "Labour",
            "description": "Skilled pipe layers - 2 persons",
            "duration": "10 days",
            "required_date": "2025-12-01",
            "status": "Delivered",
            "progress": "70%"
        },
        {
            "id": "RES-003",
            "activity": "Valve Chamber Construction",
            "activity_id": "ACT-1003",
            "resource_type": "Traffic Management",
            "description": "Full road closure with diversion",
            "duration": "7 days",
            "required_date": "2025-12-10",
            "status": "Scheduled",
            "progress": "50%"
        },
        {
            "id": "RES-004",
            "activity": "Service Connection - Block A",
            "activity_id": "ACT-1004",
            "resource_type": "Labour",
            "description": "Service layer - 1 person",
            "duration": "3 days",
            "required_date": "2025-11-28",
            "status": "Completed",
            "progress": "100%"
        },
        {
            "id": "RES-005",
            "activity": "Meter Installation - Commercial",
            "activity_id": "ACT-1005",
            "resource_type": "Specialist Equipment",
            "description": "Electrofusion welding equipment",
            "duration": "2 days",
            "required_date": "2025-11-29",
            "status": "Delivered",
            "progress": "80%"
        },
        {
            "id": "RES-006",
            "activity": "Road Reinstatement",
            "activity_id": "ACT-1006",
            "resource_type": "Specialist Contractor",
            "description": "Road surfacing contractor",
            "duration": "3 days",
            "required_date": "2025-12-19",
            "status": "Requested",
            "progress": "10%"
        }
    ];

    const metrics = {
        activity: {
            type: 'component',
            element: ((row: { activity: string; activity_id: string; }) => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'start'}>
                    <Typography variant="body2">{row.activity}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.activity_id}</Typography>
                </Box>
            ))
        },
        status: {
            type: 'component',
            element: ((row: { status: string; }) => (
                <StatusChip label={row.status} color={
                    row.status === 'Requested' ? 'default' :
                        row.status === 'Approved' ? 'blue' :
                            row.status === 'Ordered' ? 'amber' :
                                row.status === 'Delivered' ? 'green' :
                                    row.status === 'Used' ? 'violet' :
                                        'default'
                } />
            ))
        },
        progress: {
            type: 'component',
            element: ((row: { progress: string; }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={parseInt(row.progress)} />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                        >{`${Math.round(parseInt(row.progress))}%`}</Typography>
                    </Box>
                </Box>
            ))
        },
        required_date: {
            type: 'date',
        }
    }

    return (
        <Box px={2}>
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                <SearchField
                    searchTerm={null}
                    handleSearch={function (event: React.ChangeEvent<HTMLInputElement>): void {
                        throw new Error("Function not implemented.");
                    }}
                    handleClearSearch={function (): void {
                        throw new Error("Function not implemented.");
                    }}
                    handleApplySearch={function (searchTerm: string): void {
                        throw new Error("Function not implemented.");
                    }}
                />
                <Box display={'flex'} alignItems={'center'} py={2} gap={2}>
                    <Button variant="outlined" color="primary" startIcon={<FilterList />} onClick={handleFilterClick}>
                        Filters
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleFilterClose}
                    >
                        <MenuItem disabled>
                            <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusToggle('requested')}>
                            <Checkbox checked={selectedStatuses.includes('requested')} />
                            Requested
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusToggle('scheduled')}>
                            <Checkbox checked={selectedStatuses.includes('scheduled')} />
                            Scheduled
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusToggle('delivered')}>
                            <Checkbox checked={selectedStatuses.includes('delivered')} />
                            Delivered
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusToggle('completed')}>
                            <Checkbox checked={selectedStatuses.includes('completed')} />
                            Completed
                        </MenuItem>
                        <MenuItem onClick={() => handleStatusToggle('closed')}>
                            <Checkbox checked={selectedStatuses.includes('closed')} />
                            Closed
                        </MenuItem>
                        <MenuItem disabled sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Type</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => handleTypeToggle('excavator')}>
                            <Checkbox checked={selectedTypes.includes('excavator')} />
                            Excavator
                        </MenuItem>
                        <MenuItem onClick={() => handleTypeToggle('labour')}>
                            <Checkbox checked={selectedTypes.includes('labour')} />
                            Labour
                        </MenuItem>
                        <MenuItem onClick={() => handleTypeToggle('traffic_management')}>
                            <Checkbox checked={selectedTypes.includes('traffic_management')} />
                            Traffic Management
                        </MenuItem>
                        <MenuItem onClick={() => handleTypeToggle('specialist_equipment')}>
                            <Checkbox checked={selectedTypes.includes('specialist_equipment')} />
                            Specialist Equipment
                        </MenuItem>
                        <MenuItem onClick={() => handleTypeToggle('specialist_contractor')}>
                            <Checkbox checked={selectedTypes.includes('specialist_contractor')} />
                            Specialist Contractor
                        </MenuItem>
                    </Menu>
                    {/* <ButtonGroup variant="text" color="primary">
                        <Button variant={currentView === 'list' ? 'contained' : 'outlined'} color="primary" startIcon={<Task />} onClick={() => setCurrentView('list')}>List</Button>
                        <Button variant={currentView === 'map' ? 'contained' : 'outlined'} color="primary" startIcon={<MapOutlined />} onClick={() => setCurrentView('map')}>Map</Button>
                    </ButtonGroup> */}
                    <Button variant="contained" color="primary" startIcon={<Add />}>
                        New Resource Request
                    </Button>
                </Box>
            </Box>
            {/* {(selectedStatuses.length > 0 || selectedTypes.length > 0) && (
                <Box display={'flex'} alignItems={'center'} gap={1} py={2} flexWrap={'wrap'}>
                    {selectedStatuses.map(status => (
                        <Chip
                            key={status}
                            label={getFilterLabel(status)}
                            onDelete={() => handleRemoveStatusFilter(status)}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                    {selectedTypes.map(type => (
                        <Chip
                            key={type}
                            label={getFilterLabel(type)}
                            onDelete={() => handleRemoveTypeFilter(type)}
                            size="small"
                            color="secondary"
                            variant="outlined"
                        />
                    ))}
                    <Button size="small" onClick={handleClearAllFilters}>
                        Clear All
                    </Button>
                </Box>
            )} */}
            <Paper elevation={1} data-background-elevation={1}>
                <MetaDrivenTable headers={headers} data={data} dataMetrics={metrics}
                    actions={[
                        {
                            label: <Edit sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string, title: string, description: string }) => {
                                // onOpenArtefactDetails(row);
                            }
                        },
                        {
                            label: <Delete sx={{ fontSize: 18 }} />,
                            handleClick: (row: { id: string, title: string, description: string }) => {
                                // onOpenArtefactDetails(row);
                            }
                        }
                    ]}
                />
            </Paper>
        </Box>
    )
}